import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Button,
  Input,
  Table,
  Row,
  Col,
  Space,
  message,
  Pagination,
  Divider,
  Badge,
  Alert,
  Select,
  Modal // AJOUTEZ CETTE LIGNE
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  CalculatorOutlined,
  EditFilled,
  DeleteFilled,
  ShoppingCartOutlined,
  RightOutlined,
  LeftOutlined,
  BarsOutlined,
  WarningOutlined,
  EyeOutlined, // AJOUTEZ CETTE LIGNE
  FilePdfOutlined // AJOUTEZ CETTE LIGNE
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../utils/axios';
import PermissionBadge from '../../components/PermissionBadge';
import PermissionButton from '../../components/PermissionButton';
import { useAuth } from '../../utils/useAuth';
import AddAchatModal from './modals/AddAchatModal';

const { Title, Text } = Typography;
const { Option } = Select;

const AchatPage = () => {
  const [achats, setAchats] = useState([]);
  const [filteredAchats, setFilteredAchats] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingAchat, setEditingAchat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    nomUsine: '',
    startDate: '',
    endDate: ''
  });
  const [calculationErrors, setCalculationErrors] = useState([]);
  const [showCalculationAlerts, setShowCalculationAlerts] = useState(false);

  const { hasPermission, userRole } = useAuth();

  // Charger les achats depuis l'API
  const fetchAchats = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/achats');
      
      if (response.data.success) {
        let achatsArray = [];
        
        if (response.data.data && response.data.data.achats) {
          achatsArray = response.data.data.achats;
        } else if (Array.isArray(response.data.data)) {
          achatsArray = response.data.data;
        } else {
          achatsArray = [response.data.data];
        }
        
        // Formater les données
        const achatsData = achatsArray.map(achat => ({
          ...achat,
          id: achat._id || achat.id,
          date: achat.date ? dayjs(achat.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
          quantite: parseFloat(achat.quantite) || 0,
          prixParTonne: parseFloat(achat.prixParTonne) || 0,
          total: parseFloat(achat.total) || 0
        }));
        
        // Vérifier les calculs
        verifyCalculations(achatsData);
        
        setAchats(achatsData);
        setFilteredAchats(achatsData);
      } else {
        message.error(response.data.message || 'Erreur lors du chargement des achats');
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des achats:', error);
      message.error('Erreur lors du chargement des achats: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Vérifier les calculs
  const verifyCalculations = (achatsList) => {
    const errors = [];
    
    achatsList.forEach((achat, index) => {
      const quantite = parseFloat(achat.quantite) || 0;
      const prixParTonne = parseFloat(achat.prixParTonne) || 0;
      const total = parseFloat(achat.total) || 0;
      const calculated = Math.round(quantite * prixParTonne);
      
      if (total !== calculated) {
        errors.push({
          index: index + 1,
          id: achat.id,
          quantite,
          prixParTonne,
          total,
          calculated,
          difference: total - calculated
        });
      }
    });
    
    setCalculationErrors(errors);
    
    if (errors.length > 0) {
      setShowCalculationAlerts(true);
    } else {
      setShowCalculationAlerts(false);
    }
  };

  // Calcul des totaux côté frontend
  const calculateTotals = () => {
    const totalTonnes = filteredAchats.reduce((sum, achat) => {
      const quantite = parseFloat(achat.quantite) || 0;
      return sum + quantite;
    }, 0);
    
    const totalMontant = filteredAchats.reduce((sum, achat) => {
      const total = parseFloat(achat.total) || 0;
      return sum + total;
    }, 0);
    
    const totalVerifie = filteredAchats.reduce((sum, achat) => {
      const quantite = parseFloat(achat.quantite) || 0;
      const prixParTonne = parseFloat(achat.prixParTonne) || 0;
      return sum + Math.round(quantite * prixParTonne);
    }, 0);
    
    return { totalTonnes, totalMontant, totalVerifie };
  };

  const { totalTonnes, totalMontant, totalVerifie } = calculateTotals();

  // Formater les montants en FCFA
  const formatMontant = (montant) => {
    const montantNum = parseFloat(montant) || 0;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montantNum);
  };

  // Formater les tonnes
  const formatTonnes = (tonnes) => {
    const tonnesNum = parseFloat(tonnes) || 0;
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(tonnesNum);
  };

  // Appliquer les filtres
  const applyFilters = () => {
    let filtered = [...achats];
    
    if (filters.nomUsine) {
      filtered = filtered.filter(achat =>
        achat.nomUsine?.toLowerCase().includes(filters.nomUsine.toLowerCase())
      );
    }
    
    if (filters.startDate) {
      filtered = filtered.filter(achat => 
        dayjs(achat.date).isAfter(dayjs(filters.startDate).subtract(1, 'day'))
      );
    }
    
    if (filters.endDate) {
      filtered = filtered.filter(achat => 
        dayjs(achat.date).isBefore(dayjs(filters.endDate).add(1, 'day'))
      );
    }
    
    setFilteredAchats(filtered);
    setCurrentPage(1);
  };

  // Gestion du changement de filtre
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Initialiser et appliquer les filtres
  useEffect(() => {
    fetchAchats();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, achats]);

  // Ouvrir modal pour ajouter/modifier
  const showModal = (achat = null) => {
    setEditingAchat(achat);
    setIsModalVisible(true);
  };

  // Fermer modal
  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingAchat(null);
    fetchAchats(); // Rafraîchir les données
  };

  // Supprimer un achat
  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Confirmer la suppression',
      content: 'Êtes-vous sûr de vouloir supprimer cet achat ?',
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          const response = await api.delete(`/api/achats/${id}`);
          if (response.data.success) {
            message.success('Achat supprimé avec succès !');
            fetchAchats();
          } else {
            message.error(response.data.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          message.error('Erreur lors de la suppression: ' + error.message);
        }
      }
    });
  };

  // Réparer les calculs incorrects
  const fixCalculationErrors = async () => {
    Modal.confirm({
      title: 'Corriger les incohérences de calcul',
      content: `Êtes-vous sûr de vouloir corriger ${calculationErrors.length} incohérence(s) de calcul ?`,
      okText: 'Corriger',
      okType: 'primary',
      cancelText: 'Annuler',
      onOk: async () => {
        setLoading(true);
        try {
          const updates = calculationErrors.map(error => {
            const nouveauTotal = Math.round(error.quantite * error.prixParTonne);
            return api.put(`/api/achats/${error.id}`, { total: nouveauTotal });
          });
          
          await Promise.all(updates);
          message.success(`${calculationErrors.length} incohérence(s) corrigée(s) !`);
          setCalculationErrors([]);
          setShowCalculationAlerts(false);
          fetchAchats();
        } catch (error) {
          console.error('Erreur lors de la correction:', error);
          message.error('Erreur lors de la correction des calculs');
        } finally {
          setLoading(false);
        }
      }
    });
  };

 // Voir le document - VERSION CORRIGÉE
const handleViewDocument = (achat) => {
  if (achat.fichierVersement) {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const fileUrl = `${API_URL}/uploads/${achat.fichierVersement}`;
    
    console.log('📁 Tentative d\'ouverture du fichier:', fileUrl);
    
    // Ouvrir dans un nouvel onglet
    const newWindow = window.open(fileUrl, '_blank');
    
    // Si la fenêtre n'ouvre pas (bloqueur de popup), afficher un message
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      message.info(
        <div>
          <p>Le fichier ne peut pas être ouvert directement.</p>
          <p>URL: <a href={fileUrl} target="_blank" rel="noopener noreferrer">{fileUrl}</a></p>
        </div>
      );
    }
  } else {
    message.info('Aucun document joint à cet achat');
  }
};

// Télécharger le document - VERSION CORRIGÉE
const handleDownloadDocument = async (achat) => {
  if (achat.fichierVersement) {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const fileUrl = `${API_URL}/uploads/${achat.fichierVersement}`;
    const fileName = achat.nomFichier || achat.fichierVersement || 'document_achat';
    
    console.log('📥 Tentative de téléchargement:', fileUrl);
    
    try {
      // Méthode 1: Utiliser une ancre avec download
      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = fileName;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      message.success('Téléchargement démarré');
    } catch (error) {
      console.error('❌ Erreur téléchargement méthode 1:', error);
      
      // Méthode 2: Ouvrir dans un nouvel onglet
      try {
        window.open(fileUrl, '_blank');
        message.info('Le fichier s\'ouvre dans un nouvel onglet');
      } catch (error2) {
        console.error('❌ Erreur téléchargement méthode 2:', error2);
        message.error('Erreur lors du téléchargement');
      }
    }
  } else {
    message.info('Aucun document à télécharger');
  }
};

  // Colonnes du tableau
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (id, record) => {
        if (!id) return '-';
        const idStr = String(id);
        const shortId = `A${idStr.substring(idStr.length - 6)}`;
        
        const hasError = calculationErrors.some(error => error.id === id);
        if (hasError) {
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <WarningOutlined style={{ color: '#faad14', marginRight: 4 }} />
              <span>{shortId}</span>
            </div>
          );
        }
        
        return shortId;
      }
    },
    {
      title: 'Nom Usine',
      dataIndex: 'nomUsine',
      key: 'nomUsine',
      width: 180,
      render: (nomUsine) => <span style={{ fontWeight: '500' }}>{nomUsine || '-'}</span>
    },
    {
      title: 'Quantité (tonnes)',
      dataIndex: 'quantite',
      key: 'quantite',
      width: 140,
      align: 'right',
      sorter: (a, b) => {
        const qtyA = parseFloat(a.quantite) || 0;
        const qtyB = parseFloat(b.quantite) || 0;
        return qtyA - qtyB;
      },
      render: (quantite) => {
        const qty = parseFloat(quantite) || 0;
        return (
          <div style={{ fontWeight: '500', textAlign: 'right' }}>
            {formatTonnes(qty)} t
          </div>
        );
      },
    },
    {
      title: 'Prix/tonne',
      dataIndex: 'prixParTonne',
      key: 'prixParTonne',
      width: 140,
      align: 'right',
      render: (prix) => formatMontant(parseFloat(prix) || 0),
      sorter: (a, b) => {
        const prixA = parseFloat(a.prixParTonne) || 0;
        const prixB = parseFloat(b.prixParTonne) || 0;
        return prixA - prixB;
      },
    },
    {
      title: 'Date d\'achat',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
      sorter: (a, b) => {
        const dateA = a.date ? dayjs(a.date).unix() : 0;
        const dateB = b.date ? dayjs(b.date).unix() : 0;
        return dateA - dateB;
      },
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 160,
      align: 'right',
      render: (total, record) => {
        const montant = parseFloat(total) || 0;
        const quantite = parseFloat(record.quantite) || 0;
        const prixParTonne = parseFloat(record.prixParTonne) || 0;
        const calcule = Math.round(quantite * prixParTonne);
        
        const isCorrect = montant === calcule;
        
        return (
          <div style={{ 
            fontWeight: 'bold', 
            color: isCorrect ? '#0a6b5a' : '#faad14',
            textAlign: 'right'
          }}>
            {formatMontant(montant)}
            {!isCorrect && (
              <div style={{ fontSize: '10px', color: '#ff4d4f', fontWeight: 'normal' }}>
                ≠ {formatMontant(calcule)}
              </div>
            )}
          </div>
        );
      },
      sorter: (a, b) => {
        const totalA = parseFloat(a.total) || 0;
        const totalB = parseFloat(b.total) || 0;
        return totalA - totalB;
      },
    },
    {
      title: 'Document',
      key: 'document',
      width: 100,
      render: (_, record) => (
        <div>
          {record.fichierVersement ? (
            <Space>
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => handleViewDocument(record)}
                style={{ color: '#1890ff' }}
                title="Voir le document"
              />
              <Button
                type="text"
                icon={<FilePdfOutlined />}
                onClick={() => handleDownloadDocument(record)}
                style={{ color: '#52c41a' }}
                title="Télécharger"
              />
            </Space>
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>Aucun</span>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditFilled />}
            onClick={() => showModal(record)}
            style={{ color: '#1890ff' }}
            title="Modifier"
          />
          <Button
            type="text"
            icon={<DeleteFilled />}
            onClick={() => handleDelete(record.id)}
            style={{ color: '#ff4d4f' }}
            title="Supprimer"
          />
        </Space>
      ),
    },
  ];

  // Données paginées
  const paginatedData = filteredAchats.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div>
      {/* En-tête */}
      <div className="mb-8">
        <div className="flex items-center mb-3">
          <div className="p-2 bg-primary-50 rounded-lg mr-4">
            <ShoppingCartOutlined className="text-primary-500 text-2xl" />
          </div>
          <div>
            <Title level={2} className="m-0">Gestion des Achats</Title>
            <Text className="text-gray-500 text-sm">
              Gérez vos achats de ciment facilement et efficacement
            </Text>
          </div>
        </div>
        <div className="h-1 w-20 bg-gradient-to-r from-primary-500 to-primary-300 rounded-full"></div>
      </div>

      {/* Alertes d'incohérence de calcul */}
      {showCalculationAlerts && calculationErrors.length > 0 && (
        <Alert
          message={
            <div>
              <WarningOutlined /> {calculationErrors.length} incohérence(s) de calcul détectée(s)
            </div>
          }
          description={
            <div>
              Certains totaux ne correspondent pas au calcul (quantité × prix par tonne).
              <div style={{ marginTop: 8 }}>
                <Button 
                  type="primary" 
                  size="small" 
                  onClick={fixCalculationErrors}
                  loading={loading}
                >
                  Corriger automatiquement
                </Button>
                <Button 
                  size="small" 
                  onClick={() => setShowCalculationAlerts(false)}
                  style={{ marginLeft: 8 }}
                >
                  Ignorer
                </Button>
              </div>
            </div>
          }
          type="warning"
          showIcon
          closable
          onClose={() => setShowCalculationAlerts(false)}
          className="mb-6"
        />
      )}

      {/* Vérification des totaux */}
      {totalMontant !== totalVerifie && (
        <Alert
          message={
            <div>
              <WarningOutlined /> Incohérence dans les totaux
            </div>
          }
          description={
            <div>
              Total affiché: {formatMontant(totalMontant)} ≠ Total calculé: {formatMontant(totalVerifie)}
              <div style={{ marginTop: 8 }}>
                <Button 
                  size="small" 
                  onClick={fetchAchats}
                  loading={loading}
                >
                  Rafraîchir les données
                </Button>
              </div>
            </div>
          }
          type="warning"
          showIcon
          closable
          className="mb-6"
        />
      )}

      {/* Barre des totaux simplifiée */}
      <div className="mb-6">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8}>
            <Card className="shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-50 rounded-lg mr-3">
                  <CalculatorOutlined className="text-blue-500 text-xl" />
                </div>
                <div>
                  <div className="text-gray-600 text-sm">Quantité Totale</div>
                  <div className="text-xl font-bold text-blue-600">
                    {formatTonnes(totalTonnes)} tonnes
                  </div>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card className="shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-50 rounded-lg mr-3">
                  <CalculatorOutlined className="text-green-500 text-xl" />
                </div>
                <div>
                  <div className="text-gray-600 text-sm">Montant Total</div>
                  <div className="text-xl font-bold text-green-600">
                    {formatMontant(totalMontant)}
                  </div>
                  <div className="text-xs text-gray-500">
                    Prix moyen: {totalTonnes > 0 ? formatMontant(totalMontant / totalTonnes) : '0'} /tonne
                  </div>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card className="shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-purple-50 rounded-lg mr-3">
                  <BarsOutlined className="text-purple-500 text-xl" />
                </div>
                <div>
                  <div className="text-gray-600 text-sm">Total Achats</div>
                  <div className="text-xl font-bold text-purple-600">
                    {filteredAchats.length}
                  </div>
                  {calculationErrors.length > 0 && (
                    <div className="text-xs text-amber-500">
                      {calculationErrors.length} avec erreur
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Barre d'actions et filtres */}  
      <Card className="modern-card mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="sm:w-1/3">
                <Input
                  placeholder="Rechercher par nom d'usine..."
                  prefix={<SearchOutlined />}
                  value={filters.nomUsine}
                  onChange={(e) => handleFilterChange('nomUsine', e.target.value)}
                  allowClear
                  className="w-full"
                  size="large"
                />
              </div>
              
              <div className="flex gap-2">
                <Input
                  type="date"
                  placeholder="Date début"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-40"
                />
                
                <Input
                  type="date"
                  placeholder="Date fin"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="w-40"
                />
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              type="default"
              icon={<CalculatorOutlined />}
              onClick={() => verifyCalculations(filteredAchats)}
              loading={loading}
            >
              Vérifier calculs
            </Button>
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="btn-primary"
              onClick={() => showModal()}
              size="large"
            >
              Nouvel Achat
            </Button>
          </div>
        </div>
      </Card>

      {/* Tableau des achats */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden modern-card">
        {/* En-tête compact */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="flex items-center">
            <ShoppingCartOutlined className="text-gray-400 mr-2" />
            <Text className="text-gray-700 font-medium">Achats enregistrés</Text>
          </div>
          <div className="flex items-center gap-2">
            {calculationErrors.length > 0 && (
              <Badge 
                count={calculationErrors.length} 
                className="bg-amber-500 font-semibold"
                style={{ padding: '0 8px' }}
                title={`${calculationErrors.length} incohérence(s) de calcul`}
              />
            )}
            <Badge 
              count={filteredAchats.length} 
              className="bg-blue-500 font-semibold"
              style={{ padding: '0 8px' }}
            />
          </div>
        </div>

        {/* Tableau compact */}
        <div className="overflow-x-auto">
          <Table
            columns={columns}
            dataSource={paginatedData}
            rowKey="id"
            pagination={false}
            loading={loading}
            size="small"
            className="[&_.ant-table-thead>tr>th]:bg-green-700 [&_.ant-table-thead>tr>th]:py-3 [&_.ant-table-thead>tr>th]:font-medium [&_.ant-table-thead>tr>th]:text-white"
            rowClassName={(record) => {
              const baseClass = "border-b border-gray-100 hover:bg-blue-50";
              const hasError = calculationErrors.some(error => error.id === record.id);
              return hasError ? `${baseClass} bg-amber-50` : baseClass;
            }}
            locale={{
              emptyText: loading ? 'Chargement...' : 'Aucun achat enregistré'
            }}
          />
        </div>

        {/* Résumé des totaux */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center">
                <CalculatorOutlined className="text-primary-500 mr-2" />
                <Text className="text-sm text-gray-700 font-semibold">Résumé :</Text>
              </div>
              
              <div className="flex items-center space-x-6">
                <div className="text-center">
                  <Text className="text-gray-500 text-sm">Quantité totale</Text>
                  <Text strong className="text-base text-primary-500">
                    {formatTonnes(totalTonnes)} t
                  </Text>
                </div>
                
                <Divider type="vertical" className="h-8 hidden sm:block" />
                
                <div className="text-center">
                  <Text className="text-gray-500 text-sm">Montant total</Text>
                  <Text strong className="text-base text-green-600">
                    {formatMontant(totalMontant)}
                  </Text>
                  {totalMontant !== totalVerifie && (
                    <div className="text-xs text-amber-500">
                      Vérifié: {formatMontant(totalVerifie)}
                    </div>
                  )}
                </div>
                
                <Divider type="vertical" className="h-8 hidden sm:block" />
                
                <div className="text-center">
                  <Text className="text-gray-500 text-sm">Total achats</Text>
                  <Text strong className="text-base text-blue-600">
                    {filteredAchats.length}
                  </Text>
                  {calculationErrors.length > 0 && (
                    <div className="text-xs text-amber-500">
                      {calculationErrors.length} erreur(s)
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Navigation rapide */}
            <div className="flex items-center space-x-2">
              <Button
                size="small"
                icon={<LeftOutlined />}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                className="border-gray-300"
              />
              <div className="bg-white border border-gray-300 rounded px-3 py-1">
                <Text className="text-sm">
                  <span className="font-semibold">{currentPage}</span> / {Math.max(1, Math.ceil(filteredAchats.length / pageSize))}
                </Text>
              </div>
              <Button
                size="small"
                icon={<RightOutlined />}
                disabled={currentPage === Math.ceil(filteredAchats.length / pageSize)}
                onClick={() => setCurrentPage(currentPage + 1)}
                className="border-gray-300"
              />
            </div>
          </div>
        </div>

        {/* Pagination complète */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <Text className="text-gray-500 text-sm">
                Affichage{' '}
                <span className="font-semibold text-gray-700">
                  {Math.min((currentPage - 1) * pageSize + 1, filteredAchats.length)}-
                  {Math.min(currentPage * pageSize, filteredAchats.length)}
                </span>{' '}
                sur{' '}
                <span className="font-semibold text-primary-500">
                  {filteredAchats.length}
                </span>{' '}
                achats
              </Text>
            </div>
            
            <div className="flex items-center space-x-4">
              <Select
                value={pageSize}
                onChange={(value) => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                size="small"
                className="w-32"
              >
                <Option value={5}>5 par page</Option>
                <Option value={10}>10 par page</Option>
                <Option value={20}>20 par page</Option>
                <Option value={50}>50 par page</Option>
                <Option value={100}>100 par page</Option>
              </Select>
              
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredAchats.length}
                onChange={(page, size) => {
                  setCurrentPage(page);
                  if (size) setPageSize(size);
                }}
                showSizeChanger={false}
                showQuickJumper
                size="small"
                className="[&_.ant-pagination-item]:rounded-full [&_.ant-pagination-item-active]:bg-green-600 [&_.ant-pagination-item-active]:border-green-600 [&_.ant-pagination-item-active>a]:text-white"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modal pour ajouter/modifier un achat */}
      <AddAchatModal
        visible={isModalVisible}
        editingAchat={editingAchat}
        onCancel={handleCancel}
        onSuccess={fetchAchats}
      />
    </div>
  );
};

export default AchatPage;